import { queryOptions } from '@tanstack/react-query'

/**
 * Per-installation values this SPA needs and must not bake into the build (task brief: "auth_url
 * and any virtual_id are per-installation configuration ... keep them configurable, hardcode
 * nothing"). Unlike Consent.Web's same-origin CloudFront proxy — which lets one build be promoted
 * dev -> stg -> prd unchanged (patient-consent.md §8) — this service's public API sits on a
 * different origin from the SPA by design (architecture/services/integration-faceheart.md §8, D-3:
 * CORS is restricted to a single configured webapp origin, which only makes sense for a
 * cross-origin caller). A Vite build-time env var would bake one environment's origin into the
 * bundle, so these are read at *runtime* from a small JSON file the deploy pipeline overwrites
 * per environment before `s3 sync` — the same "build once, template after" shape, moved from HTML
 * substitution to a fetched document because there is more than one string to place.
 */
export interface RuntimeConfig {
  readonly scanApiBaseUrl: string
  readonly authApiBaseUrl: string
  readonly faceHeartAuthUrl: string
  readonly faceHeartVirtualId: string | null
  /**
   * How a `HybridMindsetShenAi` scan runs its two SDKs over the one camera (KAN-3).
   *
   * `sequential` - absent means this - is D-42's: Shen.AI alone, its reading submitted, its runtime
   * released, then Mindset on a clone of the same camera. `concurrent` is D-37's, restored for
   * measuring rather than shipping: both engines over the same frames, and the reading composed from
   * the two. Per installation rather than per scan because it is a question about devices, not about
   * patients: the webapp module's `hybrid_mode` (docs/decisions.md D-65), `concurrent` on us-dev only.
   */
  readonly hybridMode?: HybridMode
  /**
   * Draws the on-screen diagnostics panel (src/lib/diagnostics.ts) - for reading a scan on a phone with
   * no debugger attached. Local stacks, and an installation whose tfvars set `diagnostics` - us-dev only,
   * because every scan the installation serves draws it (docs/decisions.md D-65).
   */
  readonly diagnostics?: boolean
  /**
   * Starts the scan's vendor runtime loading on the start screen, while the patient reads it, instead of
   * after their tap (docs/decisions.md D-63). Absent means off: the single-vendor paths load exactly as
   * they always have until an installation asks for this (`sdk_warm_up`, us-dev only - D-65). Which
   * runtimes are warmed follows the vendor and `hybridMode` - never two at once on the sequential hybrid,
   * which exists to keep them apart.
   */
  readonly sdkWarmUp?: boolean
  /**
   * Which of Mindset's vitals a CONCURRENT hybrid scan asks for: `PR`, `RR`, `BP`, all three when absent.
   * A diagnostic for a local stack (docs/decisions.md D-62) - pressure brings up a second ONNX Runtime heap
   * in Mindset's worker, and this is how a phone is asked whether that is what it cannot hold. No deployed
   * config.json names it.
   */
  readonly hybridMindsetVitals?: readonly ('PR' | 'RR' | 'BP')[]
  /**
   * The frame rate a hybrid scan's shared camera asks for, in place of D-38's 30. A diagnostic for a local
   * stack: on an Android tablet Shen.AI measured alone (60) and never started beside Mindset (30)
   * (docs/decisions.md D-62). No deployed config.json names it.
   */
  readonly hybridCameraFrameRate?: number
  readonly datadogClientToken?: string
  readonly environment?: string
  readonly version?: string
}

export type HybridMode = 'sequential' | 'concurrent'

function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.scanApiBaseUrl === 'string' &&
    typeof candidate.authApiBaseUrl === 'string' &&
    typeof candidate.faceHeartAuthUrl === 'string' &&
    (candidate.faceHeartVirtualId === null || typeof candidate.faceHeartVirtualId === 'string') &&
    (candidate.hybridMode === undefined ||
      candidate.hybridMode === 'sequential' ||
      candidate.hybridMode === 'concurrent') &&
    (candidate.diagnostics === undefined || typeof candidate.diagnostics === 'boolean') &&
    (candidate.sdkWarmUp === undefined || typeof candidate.sdkWarmUp === 'boolean') &&
    (candidate.hybridMindsetVitals === undefined ||
      (Array.isArray(candidate.hybridMindsetVitals) &&
        candidate.hybridMindsetVitals.length > 0 &&
        candidate.hybridMindsetVitals.every((family) => family === 'PR' || family === 'RR' || family === 'BP'))) &&
    (candidate.hybridCameraFrameRate === undefined ||
      (typeof candidate.hybridCameraFrameRate === 'number' &&
        Number.isInteger(candidate.hybridCameraFrameRate) &&
        candidate.hybridCameraFrameRate >= 15 &&
        candidate.hybridCameraFrameRate <= 120)) &&
    isOptionalString(candidate.datadogClientToken) &&
    isOptionalString(candidate.environment) &&
    isOptionalString(candidate.version)
  )
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string'
}

export const runtimeConfigQuery = queryOptions({
  queryKey: ['runtime-config'],
  queryFn: () => loadRuntimeConfig(),
  staleTime: Infinity,
  retry: false,
  retryOnMount: false,
})

export class RuntimeConfigError extends Error {}

export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = fetch,
): Promise<RuntimeConfig> {
  const response = await fetchImpl('/config.json', { cache: 'no-store' })
  if (!response.ok) {
    throw new RuntimeConfigError(`/config.json answered ${response.status}`)
  }
  const body: unknown = await response.json()
  if (!isRuntimeConfig(body)) {
    throw new RuntimeConfigError('/config.json is not shaped like a RuntimeConfig')
  }
  return body
}
